import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { checkMap, getMapStatus } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useAsync } from "@/lib/useAsync";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { CapabilityLink, StandardLink } from "@/components/RegistryLinks";
import { ago } from "@/lib/time";
import type { MapStatus } from "@/api/types";

// Whether a map is still current, on its Review and Delivery brief pages (docs/tools-catalog-
// design.md, "Staleness and remapping"): what it references that changed since it read the
// catalog, what's new since that might help (the relevance check), and the remap that follows
// from either. Maps are write-once, so a remap is a new map that supersedes this one — made on
// the Map a change page, which Remap opens prefilled with this map's request.

const POLL_MS = 3000;
// A relevance check is one small model call; stop waiting for it after this long.
const CHECK_WAIT_MS = 3 * 60 * 1000;

const errorText = (e: unknown) => (e instanceof ApiError ? e.errors.join("; ") : String(e));

/** A link to whatever kind of entry changed. */
function EntryLink({
  registry,
  id,
  name,
}: {
  registry: MapStatus["stale"][number]["registry"];
  id: string;
  name: string;
}) {
  if (registry === "capabilities") return <CapabilityLink id={id} />;
  if (registry === "standards") return <StandardLink id={id}>{name}</StandardLink>;
  const path = registry === "tools" ? "tools" : "practices";
  return (
    <Link to={`/${path}/${encodeURIComponent(id)}`} className="text-accent underline">
      {name}
    </Link>
  );
}

/** The freshness panel for one map. Shows nothing beyond a quiet line when the map is current
 * and nothing new needs checking. `showStale={false}` leaves out the "changed since" list, for
 * a page that shows its own (a delivery brief, whose staleness follows the reviewed map). */
export function MapFreshness({ mapId, showStale = true }: { mapId: string; showStale?: boolean }) {
  const state = useAsync(() => getMapStatus(mapId), [mapId]);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"check" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // useAsync hands back a new object every render; the poller below only needs `reload`, held
  // here so the timer isn't restarted on each render.
  const reload = useRef(state.reload);
  reload.current = state.reload;

  // While a check runs, re-read the status until a newer check lands (or we give up).
  const checkedAt = state.status === "ready" ? (state.data.check?.checked_at ?? null) : null;
  useEffect(() => {
    if (busy !== "check") return;
    const started = Date.now();
    const timer = window.setInterval(async () => {
      try {
        const s = await getMapStatus(mapId);
        if ((s.check?.checked_at ?? null) !== checkedAt || Date.now() - started > CHECK_WAIT_MS) {
          window.clearInterval(timer);
          setBusy(null);
          reload.current();
        }
      } catch (e) {
        window.clearInterval(timer);
        setBusy(null);
        setError(errorText(e));
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [busy, mapId, checkedAt]);

  if (state.status !== "ready") return null;
  const s = state.data;

  /** "Check now": start the relevance check, then wait for its result (effect above). */
  async function check() {
    setError(null);
    try {
      await checkMap(mapId);
      setBusy("check");
    } catch (e) {
      setError(errorText(e));
    }
  }

  const helps = s.check?.helps ?? [];
  const checkCurrent = s.check?.up_to_date ?? false;
  const unchecked = s.new_since.length > 0 && !checkCurrent;
  const offerRemap =
    !s.superseded_by && (s.stale.length > 0 || s.predates_suggestions || (checkCurrent && helps.length > 0));

  return (
    <section className="flex flex-col gap-2 text-sm">
      {s.supersedes && (
        <p className="text-text/70">
          Remap of{" "}
          <Link to={`/review/${encodeURIComponent(s.supersedes)}`} className="text-accent underline">
            {s.supersedes_title ?? "an earlier map"}
          </Link>
          {s.remap_reason && <> — {s.remap_reason}</>}
        </p>
      )}
      {s.superseded_by && (
        <p className="rounded border border-line bg-sunken p-2.5">
          Superseded by{" "}
          <Link to={`/review/${encodeURIComponent(s.superseded_by)}`} className="font-medium text-accent underline">
            {s.superseded_by_title ?? "a newer map"}
          </Link>
          , a remap of this map. This one is kept with its review.
        </p>
      )}

      {s.predates_suggestions && !s.superseded_by && (
        <p className="rounded border border-line bg-sunken p-2.5">
          Made before maps suggested tools and practices. Remap it to get suggestions from today's catalog.
        </p>
      )}

      {showStale && s.stale.length > 0 && (
        <div className="rounded border border-warn-line bg-warn-bg p-2.5">
          <p>
            <strong className="text-warn">Stale.</strong> Changed since this map read the catalog (
            {ago(s.catalog_read_at ?? "")}):
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {s.stale.map((g) => (
              <li key={g.registry}>
                <span className="text-text/60">{g.registry}:</span>{" "}
                {g.changed.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && ", "}
                    <EntryLink registry={g.registry} id={c.id} name={c.name} />
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {checkCurrent && helps.length > 0 && (
        <div className="rounded border border-accent-line bg-accent-bg p-2.5">
          <p>
            <strong className="text-accent">New since this map, and may help:</strong>
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {helps.map((h) => (
              <li key={`${h.type}:${h.id}`}>
                <Link
                  to={`/${h.type === "tool" ? "tools" : "practices"}/${encodeURIComponent(h.id)}`}
                  className="font-medium text-accent underline"
                >
                  {h.name}
                </Link>
                {h.ring && <span className="text-text/60"> ({h.ring})</span>} — {h.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(unchecked || offerRemap || (checkCurrent && helps.length === 0 && s.new_since.length > 0)) && (
        <div className="flex flex-wrap items-center gap-3">
          {checkCurrent && helps.length === 0 && s.new_since.length > 0 && (
            <span className="text-text/60">
              Checked {ago(s.check!.checked_at)}: none of the {s.check!.considered} new or changed entries would help.
            </span>
          )}
          {unchecked && (
            <span className="text-text/60">
              {s.new_since.length} {s.new_since.length === 1 ? "entry" : "entries"} added or changed since this map
              {s.live ? " — checked automatically after the next catalog sync." : "."}
            </span>
          )}
          {unchecked && !s.superseded_by && (
            <Button onClick={check} isDisabled={busy !== null}>
              {busy === "check" ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-3.5 w-3.5" /> Checking…
                </span>
              ) : (
                "Check now"
              )}
            </Button>
          )}
          {offerRemap && (
            <Button variant="primary" onClick={() => navigate(`/map?remap=${encodeURIComponent(mapId)}`)}>
              Remap
            </Button>
          )}
        </div>
      )}
      {error && <p className="text-warn">{error}</p>}
    </section>
  );
}
